/**
 * Strategic Roadmap Template Form
 * Long-term planning, milestones, and strategic initiatives
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const StrategicRoadmapForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    company_name: '',
    roadmap_name: '',
    fiscal_year: new Date().getFullYear().toString(),
    date: new Date().toISOString().split('T')[0],
    prepared_by: '',
    approved_by: '',
    vision: '',
    mission: '',
    strategic_goals: [''],
    initiatives: [{
      id: 'I-001',
      initiative: '',
      description: '',
      category: '',
      quarter: 'Q1',
      year: new Date().getFullYear().toString(),
      priority: 'Medium',
      status: 'Planned',
      progress: 0,
      owner: '',
      dependencies: '',
      resources_needed: '',
      expected_outcome: '',
      kpis: '',
      budget: ''
    }],
    rai_assignments: [],
    followups: []
  });

  const getSampleData = () => ({
    company_name: 'TechCorp Inc.',
    roadmap_name: 'Product & Engineering Roadmap',
    fiscal_year: '2025',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'VP of Product',
    approved_by: 'CEO',
    vision: 'To become the leading provider of enterprise collaboration solutions in North America by 2027',
    mission: 'Empower teams to work smarter through innovative technology and seamless integration',
    strategic_goals: [
      'Achieve $50M ARR by end of 2025',
      'Expand to 3 new geographic markets',
      'Launch AI-powered features across all products',
      'Improve NPS score to 70+'
    ],
    initiatives: [
      { id: 'I-001', initiative: 'AI Assistant Launch', description: 'Integrate GPT-powered assistant into main product', category: 'Product', quarter: 'Q1', year: '2025', priority: 'Critical', status: 'In Progress', progress: 35, owner: 'Product Lead', dependencies: 'AI Infrastructure completion', resources_needed: '3 Engineers, 1 PM, $150K API costs', expected_outcome: '20% increase in user engagement', kpis: 'DAU, Feature adoption rate', budget: '$300,000' },
      { id: 'I-002', initiative: 'Enterprise SSO', description: 'Implement SAML/OIDC authentication for enterprise customers', category: 'Engineering', quarter: 'Q1', year: '2025', priority: 'High', status: 'Planned', progress: 0, owner: 'Security Lead', dependencies: 'None', resources_needed: '2 Engineers, Security audit', expected_outcome: 'Unlock enterprise deals worth $2M', kpis: 'Enterprise customer acquisition', budget: '$75,000' },
      { id: 'I-003', initiative: 'APAC Market Entry', description: 'Establish presence in Singapore and Japan markets', category: 'Business', quarter: 'Q2', year: '2025', priority: 'High', status: 'Planned', progress: 0, owner: 'VP Sales', dependencies: 'Legal entity setup', resources_needed: 'Regional team, $500K budget', expected_outcome: '$5M new ARR from APAC', kpis: 'APAC revenue, customer count', budget: '$500,000' },
      { id: 'I-004', initiative: 'Platform Scalability', description: 'Re-architect backend for 10x user growth', category: 'Engineering', quarter: 'Q2', year: '2025', priority: 'Critical', status: 'Not Started', progress: 0, owner: 'CTO', dependencies: 'AI Assistant completion', resources_needed: '5 Engineers, Infrastructure upgrade', expected_outcome: 'Support 1M concurrent users', kpis: 'Response time, Uptime', budget: '$250,000' },
      { id: 'I-005', initiative: 'Mobile App v2', description: 'Complete redesign of mobile experience', category: 'Product', quarter: 'Q3', year: '2025', priority: 'Medium', status: 'Not Started', progress: 0, owner: 'Mobile Lead', dependencies: 'Design system update', resources_needed: '4 Mobile devs, 1 Designer', expected_outcome: '4.5+ app store rating', kpis: 'App rating, Mobile DAU', budget: '$200,000' },
      { id: 'I-006', initiative: 'Customer Success Program', description: 'Launch proactive customer success program', category: 'Operations', quarter: 'Q3', year: '2025', priority: 'Medium', status: 'Not Started', progress: 0, owner: 'VP Customer Success', dependencies: 'CRM integration', resources_needed: '3 CSMs, Tools budget', expected_outcome: 'Reduce churn by 20%', kpis: 'Churn rate, NPS', budget: '$150,000' }
    ],
    rai_assignments: [
      { task: 'Finalize Q1 sprint planning', responsible: 'Engineering Managers', accountable: 'CTO', informed: 'CEO, Board', due_date: '2025-01-15', status: 'In Progress' },
      { task: 'Complete APAC legal setup', responsible: 'Legal Counsel', accountable: 'CFO', informed: 'VP Sales', due_date: '2025-03-01', status: 'Pending' }
    ],
    followups: [
      { item: 'Monthly roadmap review meeting', owner: 'VP Product', due_date: '2025-01-31', priority: 'High', status: 'Pending' },
      { item: 'Budget approval for Q2 initiatives', owner: 'CFO', due_date: '2025-02-15', priority: 'Critical', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Strategic Roadmap Draft";
      const payload = {
        name: draftName,
        template_type: "Strategic Roadmap",
        template_id: "strategic-roadmap",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "strategic-roadmap", "Strategic Roadmap", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("strategic-roadmap", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/strategic-roadmap`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Strategic_Roadmap_${formData.company_name.replace(/\s+/g, '_') || 'Document'}_${formData.fiscal_year}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Strategic Roadmap",
        formData,
        settings: activeSettings || {},
        templateType: "Strategic Roadmap",
        templateId: "strategic-roadmap",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addInitiative = () => {
    const newId = `I-${String(formData.initiatives.length + 1).padStart(3, '0')}`;
    setFormData({
      ...formData,
      initiatives: [...formData.initiatives, {
        id: newId, initiative: '', description: '', category: '', quarter: 'Q1', year: formData.fiscal_year,
        priority: 'Medium', status: 'Planned', progress: 0, owner: '', dependencies: '', resources_needed: '',
        expected_outcome: '', kpis: '', budget: ''
      }]
    });
  };

  const updateInitiative = (index, field, value) => {
    const initiatives = [...formData.initiatives];
    initiatives[index] = { ...initiatives[index], [field]: value };
    setFormData({ ...formData, initiatives });
  };

  const removeInitiative = (index) => {
    setFormData({ ...formData, initiatives: formData.initiatives.filter((_, i) => i !== index) });
  };

  const addGoal = () => setFormData({ ...formData, strategic_goals: [...formData.strategic_goals, ''] });
  const updateGoal = (index, value) => {
    const goals = [...formData.strategic_goals];
    goals[index] = value;
    setFormData({ ...formData, strategic_goals: goals });
  };
  const removeGoal = (index) => setFormData({ ...formData, strategic_goals: formData.strategic_goals.filter((_, i) => i !== index) });

  const categories = ['Product', 'Engineering', 'Business', 'Operations', 'Marketing', 'Sales', 'Finance', 'HR', 'Other'];
  const priorities = ['Critical', 'High', 'Medium', 'Low'];
  const statuses = ['Not Started', 'Planned', 'In Progress', 'On Hold', 'Completed', 'Cancelled'];
  const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Strategic Roadmap</h1>
                <p className="text-sm text-gray-500">Long-term planning, milestones, and strategic initiatives</p>
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Sample Data
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Reset
              </Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Company & Roadmap Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Company Name *</Label>
                <Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} placeholder="Company name" />
              </div>
              <div>
                <Label>Roadmap Name *</Label>
                <Input value={formData.roadmap_name} onChange={(e) => setFormData({...formData, roadmap_name: e.target.value})} placeholder="e.g., Product Roadmap 2025" />
              </div>
              <div>
                <Label>Fiscal Year</Label>
                <Input value={formData.fiscal_year} onChange={(e) => setFormData({...formData, fiscal_year: e.target.value})} placeholder="2025" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Date</Label>
                <Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} />
              </div>
              <div>
                <Label>Prepared By</Label>
                <Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} placeholder="Your name/role" />
              </div>
              <div>
                <Label>Approved By</Label>
                <Input value={formData.approved_by} onChange={(e) => setFormData({...formData, approved_by: e.target.value})} placeholder="Approver name" />
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Vision Statement</Label>
                <Textarea value={formData.vision} onChange={(e) => setFormData({...formData, vision: e.target.value})} placeholder="Where we want to be..." rows={3} />
              </div>
              <div>
                <Label>Mission Statement</Label>
                <Textarea value={formData.mission} onChange={(e) => setFormData({...formData, mission: e.target.value})} placeholder="How we will get there..." rows={3} />
              </div>
            </div>

            <div>
              <Label className="font-semibold">Strategic Goals</Label>
              {formData.strategic_goals.map((goal, idx) => (
                <div key={idx} className="flex gap-2 mt-2">
                  <Input value={goal} onChange={(e) => updateGoal(idx, e.target.value)} placeholder={`Strategic goal ${idx + 1}`} />
                  <Button variant="ghost" size="icon" onClick={() => removeGoal(idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addGoal} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add Goal</Button>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold text-lg">Strategic Initiatives</Label>
              <p className="text-xs text-gray-500 mb-4">Define key initiatives, owners, timelines, and expected outcomes</p>
              
              <div className="space-y-4">
                {formData.initiatives.map((item, idx) => (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg border space-y-3">
                    <div className="flex justify-between items-start">
                      <span className="text-sm font-medium text-gray-500">Initiative #{idx + 1}</span>
                      <Button variant="ghost" size="icon" onClick={() => removeInitiative(idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs">ID</Label>
                        <Input value={item.id} onChange={(e) => updateInitiative(idx, 'id', e.target.value)} placeholder="I-001" className="text-sm" />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs">Initiative Name *</Label>
                        <Input value={item.initiative} onChange={(e) => updateInitiative(idx, 'initiative', e.target.value)} placeholder="Initiative name" className="text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs">Category</Label>
                        <Select value={item.category} onValueChange={(v) => updateInitiative(idx, 'category', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs">Description</Label>
                      <Textarea value={item.description} onChange={(e) => updateInitiative(idx, 'description', e.target.value)} placeholder="Detailed description of the initiative" rows={2} className="text-sm" />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                      <div>
                        <Label className="text-xs">Quarter</Label>
                        <Select value={item.quarter} onValueChange={(v) => updateInitiative(idx, 'quarter', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{quarters.map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Year</Label>
                        <Input value={item.year} onChange={(e) => updateInitiative(idx, 'year', e.target.value)} className="text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs">Priority</Label>
                        <Select value={item.priority} onValueChange={(v) => updateInitiative(idx, 'priority', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Status</Label>
                        <Select value={item.status} onValueChange={(v) => updateInitiative(idx, 'status', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Progress %</Label>
                        <Input type="number" min="0" max="100" value={item.progress} onChange={(e) => updateInitiative(idx, 'progress', parseInt(e.target.value) || 0)} className="text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs">Owner</Label>
                        <Input value={item.owner} onChange={(e) => updateInitiative(idx, 'owner', e.target.value)} placeholder="Owner" className="text-sm" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Dependencies</Label>
                        <Input value={item.dependencies} onChange={(e) => updateInitiative(idx, 'dependencies', e.target.value)} placeholder="What needs to happen first" className="text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs">Resources Needed</Label>
                        <Input value={item.resources_needed} onChange={(e) => updateInitiative(idx, 'resources_needed', e.target.value)} placeholder="People, budget, tools" className="text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs">Budget</Label>
                        <Input value={item.budget} onChange={(e) => updateInitiative(idx, 'budget', e.target.value)} placeholder="$100,000" className="text-sm" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Expected Outcome</Label>
                        <Input value={item.expected_outcome} onChange={(e) => updateInitiative(idx, 'expected_outcome', e.target.value)} placeholder="Measurable outcome" className="text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs">Success KPIs</Label>
                        <Input value={item.kpis} onChange={(e) => updateInitiative(idx, 'kpis', e.target.value)} placeholder="Key metrics to track" className="text-sm" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addInitiative} className="mt-3">
                <Plus className="h-4 w-4 mr-1" /> Add Initiative
              </Button>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default StrategicRoadmapForm;
